<template>
  <block-item-card
    :min-height="minBlockHeight"
    :title="$t('RiskLiquidity.Section.BankFailures.Blocks.USBankFailures.Title')"
    :tooltip="$t('RiskLiquidity.Section.BankFailures.Blocks.USBankFailures.InfoTip')"
    class="gradient-grey-06-bg"
  >
    <template #actions>
      <div
        class="d-flex flex-column align-end"
        style="margin-left: auto; min-width: 0;"
      >
        <div class="synthesis-ui-red-02--text font-weight-bold text-right">
          <span class="text-size-28">{{ totalCount }}</span>
          <span class="text-size-14 ml-1">{{ $t('RiskLiquidity.Section.BankFailures.Blocks.USBankFailures.Failures') }}</span>
        </div>
      </div>
    </template>

    <v-row
      class="ma-0 pt-0 pb-4 px-2"
      no-gutters
    >
      <v-col
        cols="12"
        class="rounded-lg"
      >
        <div class="chart-container">
          <div class="chart-header">
            <asset-button-group
              :height="26"
              :items="usBankFailuresFilterItems"
              :selected.sync="selectedFilter"
              disable-dropdown-on-mobile
            />
            <div
              class="d-flex flex-row"
              style="gap:16px;"
            >
              <div
                v-if="selectedFilter == 'Total'"
                class="d-flex flex-row align-center"
                style="gap:10px;max-width:220px;border:1px solid var(--v-synthesis-ui-grey-02-base);padding:11px 16px;"
              >
                <banking-switch
                  v-model="filters.showAnnotations"
                  :enabled-label="$t('RiskLiquidity.Section.BankFailures.Blocks.USBankFailures.Toggles.ShowAnnotations')"
                  :disabled-label="$t('RiskLiquidity.Section.BankFailures.Blocks.USBankFailures.Toggles.HideAnnotations')"
                />
              </div>
              <div
                class="d-flex flex-row align-center"
                style="gap:10px;max-width:220px;border:1px solid var(--v-synthesis-ui-grey-02-base);padding:11px 16px;"
              >
                <banking-switch
                  v-model="filters.totalValueOfAssets"
                  :enabled-label="$t('RiskLiquidity.Section.BankFailures.Blocks.USBankFailures.Toggles.TotalValueOfAssets')"
                  :disabled-label="$t('RiskLiquidity.Section.BankFailures.Blocks.USBankFailures.Toggles.AmountOfFailures')"
                />
              </div>
            </div>
          </div>
          <v-lazy>
            <div class="chart-fixed-wrap">
              <e-chart
                :key="`echart-us-bank-failures-${selectedFilter}-${filters.totalValueOfAssets}`"
                :ref="`echart-us-bank-failures`"
                :autoresize="true"
                :option="graphData"
                :style="`height: 350px;`"
                class="chart"
              />
            </div>
          </v-lazy>
        </div>
      </v-col>
    </v-row>
  </block-item-card>
</template>

        <script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import AssetButtonGroup from '@/theme/default/components/common/AssetButtonGroup'
import BankingSwitch from '@/theme/default/components/common/BankingSwitch'
import API from '@/api/Api'
import Auth from '@/mixins/auth'
import { clone, toNumberFormatNotationShort  }                         from '@/lib/utils/helper'
import { use }                 from 'echarts/core'
import { LineChart, ScatterChart, CustomChart } from 'echarts/charts'

import {
  GridComponent, TooltipComponent, LegendComponent,
  MarkPointComponent, MarkLineComponent, GraphicComponent
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

use([
  LineChart,
  GridComponent, TooltipComponent, LegendComponent,
  MarkPointComponent, MarkLineComponent, GraphicComponent,
  CanvasRenderer,
  ScatterChart,
  CustomChart
])

export default {
  name      : 'UsBankFailures',
  components: {
    BlockItemCard,
    AssetButtonGroup,
    BankingSwitch
  },
  dataStore: {
    UsBankFailures: 'RiskLiquidity.BankFailures.UsBankFailures'
  },
  mixins: [chartsColorMixin, Auth],
  props : {},
  data () {
    return {
      selectedFilter: 'Total',
      minBlockHeight: 350,
      filters       : {
        showAnnotations   : false,
        totalValueOfAssets: false
      },
      usBankFailuresFilterItems: [
        {
          Title: this.$t('RiskLiquidity.Section.BankFailures.Blocks.USBankFailures.Filters.Total'),
          Value: 'Total'
        },
        ...['2000-2004', '2005-2009', '2010-2014', '2015-2019', '2020-2024', '2025+'].map(period => ({
          Title: period,
          Value: period
        }))
      ]
    }
  },
  computed: {
    baseCurrency () {
      return this.UsBankFailures?.Data?.baseCurrency || null
    },
    items () {
      return this.UsBankFailures?.Data?.items || []
    },
    totalCount () {
      return this.UsBankFailures?.Data?.totalCount || 0
    },
    annotations () {
      return this.UsBankFailures?.Data?.annotations || []
    },
    currencySymbol () {
      return this.baseCurrency?.displaySymbol || ''
    },
    formattedDates () {
      if (!this.items) return []
      return this.items.map(item => {
        return item.label
      })
    },

    selectedDates () {
      switch (this.selectedFilter) {
        case '2000-2004':
          return { fromDate: '2000-01-01', toDate: '2004-12-31' }
        case '2005-2009':
          return { fromDate: '2005-01-01', toDate: '2009-12-31' }
        case '2010-2014':
          return { fromDate: '2010-01-01', toDate: '2014-12-31' }
        case '2015-2019':
          return { fromDate: '2015-01-01', toDate: '2019-12-31' }
        case '2020-2024':
          return { fromDate: '2020-01-01', toDate: '2024-12-31' }
        case '2025+':
          return { fromDate: '2025-01-01', toDate: new Date().toISOString().split('T')[0] }
        case 'Total':
        default:
          return { fromDate: '2000-01-01', toDate: new Date().toISOString().split('T')[0] }
      }
    },

    totalGraphData  () {
      if (!this.items) return {}

      const displayDataKey = this.filters.totalValueOfAssets ? 'assets' : 'count'

      const seriesData = (this.items || []).map(it => [
        this.qStartISO(it.year, it.quarter),
        it[displayDataKey],
        it.label
      ])

      const minX = '2000-01-01'
      const maxX = new Date().toISOString().slice(0, 10)

      const annoYears = this.filters.showAnnotations ? (this.annotations || []).map(a => `${a.year}`) : []

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            if (!params[0].value || params[0].value === '-') {
              return null // disables tooltip + pointer
            }
            const [, v, label] = params[0].value

            const formattedV = this.filters.totalValueOfAssets ? this.formatNumberShort(v) : v + ' ' + (v === 1 ? this.$t('RiskLiquidity.Section.BankFailures.Blocks.USBankFailures.Failure') : this.$t('RiskLiquidity.Section.BankFailures.Blocks.USBankFailures.Failures'))

            return `
                      <div style="margin: 2px 0;">
                          <div style="font-size:12px;">${label}</div>
                          <div style="font-weight:700;">${formattedV}</div>
                      </div>`
          }
        },
        legend: { show: false },
        grid  : {
          margin      : -15,
          left        : 55,
          top         : 50,
          right       : 20,
          bottom      : 10,
          containLabel: true
        },
        xAxis: {
          type       : 'time',
          min        : minX,
          max        : maxX,
          boundaryGap: false,
          scale      : false,
          axisLine   : {
            show: false
          },
          axisLabel: {
            color    : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize : 12,
            formatter: (val) => {
              const d = new Date(val)
              const y = d.getFullYear()
              const m = d.getMonth()
              return (m === 0 && y % 5 === 0) ? `Jan ${y}` : ''
            }
          },
          minInterval: 5 * 365 * 24 * 3600 * 1000,
          maxInterval: 5 * 365 * 24 * 3600 * 1000

        },
        yAxis: {
          type     : 'value',
          axisLabel: {
            margin       : -30,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom',
            formatter    : (value) => {
              if (!value && this.filters.totalValueOfAssets) return this.currencySymbol + '0'
              return this.filters.totalValueOfAssets ? this.formatNumberShort(value) : value
            }
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }
        },
        series: [{
          name     : 'Bank Failures',
          type     : 'line',
          data     : seriesData,
          itemStyle: {
            color: this.themeColorRGBA('synthesis-ui-blue-04')
          },
          lineStyle: {
            width: 3
          },
          symbol    : 'emptyCircle',
          symbolSize: 0,
          markLine  : {
            symbol   : 'none',
            label    : { show: false },
            lineStyle: { type: 'dashed', width: 2, color: this.themeColorRGBA('synthesis-ui-red-06') },
            data     : annoYears.map(d => ({ xAxis: d }))
          },
          markPoint: {
            symbol    : this.filters.showAnnotations ? 'circle' : 'none',
            symbolSize: 10,
            itemStyle : {
              color      : this.themeColorRGBA('synthesis-ui-red-03'),
              borderColor: '#fff',
              borderWidth: 1
            },
            data: (this.annotations || []).map(a => {
              const match = this.items.find(it => it.year === a.year)
              const quarter = match ? match.quarter : 'Q1'
              const yVal = match ? match.count : 0
              return {
                coord: [this.qStartISO(a.year, quarter), yVal],
                label: {
                  show     : true,
                  formatter: () => `{title|${a.year}}\n{desc|${a.name}}`,
                  rich     : {
                    title: {
                      fontSize  : 13,
                      fontWeight: 'bold',
                      padding   : 6
                    },
                    desc: {
                      padding: 6
                    }
                  },
                  backgroundColor: '#fff',
                  borderColor    : '#E74C3C',
                  borderWidth    : 1,
                  borderRadius   : 6,
                  padding        : [6, 8],
                  position       : 'top',
                  offset         : [40, -20]
                }
              }
            })
          }
        }]
      }
    },

    assetsGraphData () {
      let [fromYear, toYear] = this.selectedFilter.split('-').map(parseInt)

      if (!toYear) {
        toYear = fromYear + 4
      }

      const pts = this.items
        .filter(it => it.year >= fromYear && it.year <= toYear)
        .map(it => ({
          dateISO: `${it.year}-${({ Q1: '01', Q2: '04', Q3: '07', Q4: '10' })[it.quarter]}-01T00:00:00Z`,
          count  : it.count,
          assets : it.assets || 0,
          label  : `${it.quarter} ${it.year}`
        }))

      const bucket = v =>
        v === 0 ? 'none'
        : v < 1e9 ? 'lt1'
          : v < 30e9 ? 'b1_30'
            : 'gt30'

      const seriesByBucket = {
        lostAssets: { name: this.$t('Lost Assets') + ':', color: 'white' },
        none      : { name: this.$t?.('None'), color: this.themeColorRGBA('synthesis-ui-blue-04') },
        lt1       : { name: this.$t ?.('Less than $1bn'), color: this.themeColorRGBA('synthesis-ui-yellow-01') },
        b1_30     : { name: this.$t?.('From $1bn to $30bn'), color: this.themeColorRGBA('synthesis-ui-orange-03') },
        gt30      : { name: this.$t?.('More than $30bn'), color: this.themeColorRGBA('synthesis-ui-red-03') }
      }

      // group data
      const grouped = { none: [], lt1: [], b1_30: [], gt30: [] }
      pts.forEach(p => grouped[bucket(p.assets)].push([p.dateISO, p.count, p.assets, p.label]))

      // a little left padding so the first bubble isn’t clipped
      const xMin = `${fromYear}-01-01`
      const xMax = `${toYear}-12-31`

      const fmtMoney = v => this.formatNumberShort(v)

      return {
        animation        : true,
        animationDuration: 800,
        grid             : { left: 55, right: 20, top: 50, bottom: 50, containLabel: true },

        tooltip: {
          trigger        : 'item',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.92),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#fff' },
          formatter      : p => {
            const [,, assets, label] = p.data
            return `
          <div style="font-size:12px;opacity:.9">${label}</div>
          <div style="display:flex;align-items:center;gap:6px;">
            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};"></span>
            <b>${fmtMoney(assets)}</b>
          </div>`
          }
        },

        legend: {
          show           : true,
          bottom         : 0,
          left           : 20,
          backgroundColor: '#FFFFFF',
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-02', 1),
          borderWidth    : 1,
          borderRadius   : 10,
          padding        : [10, 16],
          icon           : 'circle',
          itemWidth      : 12,
          itemHeight     : 12,
          itemGap        : 28,

          // text style
          textStyle: {
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 14
          },

          data: [
            seriesByBucket.lostAssets.name,
            seriesByBucket.none.name,
            seriesByBucket.lt1.name,
            seriesByBucket.b1_30.name,
            seriesByBucket.gt30.name
          ],

          formatter: (name) => {
            return name
          }
        },

        xAxis: {
          type    : 'time',
          min     : xMin,
          max     : xMax,
          axisLine: {
            show: false
          },
          axisLabel: {
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 12,
            margin  : 16
          },
          minInterval: 365 * 24 * 3600 * 1000,
          maxInterval: 365 * 24 * 3600 * 1000,
          splitNumber: toYear - fromYear + 1
        },

        yAxis: {
          type     : 'value',
          axisLabel: {
            inside       : true,
            verticalAlign: 'bottom',
            margin       : -30,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: { show: true, lineStyle: { color: this.themeColorRGBA('synthesis-ui-grey-02'), type: 'dashed', width: 2 } }
        },

        series: [
          {
            id        : 'stems',
            type      : 'custom',
            name      : 'Stems',
            data      : pts.map(p => [p.dateISO, p.count]),
            renderItem: (params, api) => {
              const xVal = api.value(0)
              const yVal = api.value(1)
              const top = api.coord([xVal, yVal])
              const bot = api.coord([xVal, 0])
              return {
                type : 'line',
                shape: { x1: bot[0], y1: bot[1], x2: top[0], y2: top[1] },
                style: { stroke: this.themeColorRGBA('synthesis-ui-blue-04'), lineWidth: 2, lineDash: [4, 6], opacity: 0.45 },
                z    : 1
              }
            },
            silent: true,
            z     : 1
          },

          ...Object.entries(seriesByBucket).map(([key, meta]) => ({
            id        : `caps-${key}`,
            type      : 'scatter',
            name      : meta.name,
            data      : grouped[key],
            symbol    : 'roundRect',
            symbolSize: val => {
              const assets = val[2]
              const [w, h] = assets <= 0 ? [4, 4]
                : assets < 1e9 ? [16, 2]
                  : assets < 30e9 ? [24, 16]
                    : [52, 24]
              return [w, h]
            },
            itemStyle: {
              color: meta.color
            },
            clip: false,
            z   : 2
          }))
        ]
      }
    },

    periodGraphData () {
      let [fromYear, toYear] = this.selectedFilter.split('-').map(parseInt)

      if (!toYear) {
        toYear = fromYear + 4
      }

      const pts = this.itemsInRange(fromYear, toYear).map(it => ({
        x: this.qStartISO(it.year, it.quarter),
        y: it.count
      }))

      const periodOption = {
        animation        : true,
        animationDuration: 800,
        grid             : { left: 60, top: 50, right: 20, bottom: 10, containLabel: true },
        tooltip          : {
          trigger        : 'item',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#fff' },
          formatter      : p => {
            const d = new Date(p.data[0])
            const y = d.getUTCFullYear()
            const q = ['Q1', 'Q2', 'Q3', 'Q4'][Math.floor(d.getUTCMonth() / 3)]
            return `<b>${q} ${y}</b><br/>Number of Failures: ${p.data[1]}`
          }
        },

        xAxis: {
          type       : 'time',
          min        : `${fromYear}-01-01`,
          max        : `${toYear}-12-31`,
          minInterval: 365 * 24 * 3600 * 1000,
          maxInterval: 365 * 24 * 3600 * 1000,
          splitNumber: toYear - fromYear + 1,
          axisLine   : {
            show: false
          },

          axisLabel: {
            color    : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize : 12,
            formatter: (val) => {
              const d = new Date(val)
              const y = d.getFullYear()
              return String(y)
            }
          },

          axisTick: {
            show    : true,
            interval: (i, val) => {
              const d = new Date(val.value ?? val)
              return d.getMonth() === 0
            }
          },

          splitLine: {
            show    : true,
            interval: (i, val) => {
              const d = new Date(val.value ?? val)
              return d.getMonth() === 0
            },
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-06', 0.3),
              type : 'dashed'
            }
          }
        },

        yAxis: {
          type     : 'value',
          axisLabel: {
            inside       : true,
            verticalAlign: 'bottom',
            margin       : -35,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11

          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: { color: this.themeColorRGBA('synthesis-ui-grey-02', 1), type: 'dashed', width: 2 }
          }
        },
        series: [
          // 1) dashed stems (custom series)
          {
            type      : 'custom',
            name      : 'Stems',
            data      : pts.map(p => [p.x, p.y]),
            renderItem: (params, api) => {
              const xVal = api.value(0)
              const yVal = api.value(1)
              const ptTop = api.coord([xVal, yVal])
              const ptBot = api.coord([xVal, 0])
              return {
                type : 'line',
                shape: { x1: ptBot[0], y1: ptBot[1], x2: ptTop[0], y2: ptTop[1] },
                style: {
                  stroke   : this.themeColorRGBA('synthesis-ui-blue-04'),
                  lineWidth: 2,
                  lineDash : [4, 6],
                  opacity  : 0.6
                },
                z: 1
              }
            },
            silent: true,
            z     : 1
          },

          // 2) value bubbles on top (scatter with label as bubble)
          {
            type      : 'scatter',
            name      : 'Amount of Failures',
            data      : pts.map(p => [p.x, p.y]),
            symbolSize: 1,
            itemStyle : { color: 'transparent' },
            emphasis  : { disabled: true },
            clip      : false,
            label     : {
              show           : true,
              formatter      : p => `{bubble|${p.data[1]}}`,
              position       : 'top',
              offset         : [0, -6],
              backgroundColor: '#FFFFFF',
              borderColor    : 'white',
              borderWidth    : 1,
              borderRadius   : 4,
              padding        : [4, 8],
              shadowBlur     : 12,
              shadowColor    : 'rgba(110, 142, 223, 0.32)',
              rich           : {
                bubble: {
                  fontSize  : 13,
                  fontWeight: 600,
                  color     : this.themeColorRGBA('synthesis-ui-blue-04')
                }
              }
            },
            z: 2
          }
        ]
      }

      return periodOption
    },

    graphData () {
      if (!this.items) return {}

      if (this.selectedFilter === 'Total') {
        return { ...this.totalGraphData }
      }

      if (this.filters.totalValueOfAssets) {
        return { ...this.assetsGraphData }
      }

      return { ...this.periodGraphData }
    },
    accountId () {
      return this.user?.selectedAccountId || null
    }
  },

  watch: {
    selectedFilter: {
      handler () {
        this.getUsBankFailures()
      }
    }
  },
  mounted () {},
  created () {
    this.getUsBankFailures()
  },
  methods: {
    formatNumberShort (value) {
      return this.currencySymbol + '' + toNumberFormatNotationShort(value)
    },

    itemsInRange (fromYear, toYear) {
      return (this.items || []).filter(it => it.year >= fromYear && it.year <= toYear)
    },
    qStartISO (year, q) {
      const m = { Q1: '01', Q2: '04', Q3: '07', Q4: '10' }[q]
      return `${year}-${m}-01`
    },
    async getUsBankFailures () {
      const response = await API.Resource.RiskLiquidity.GetUsBankFailures(this.accountId, { ...this.selectedDates })
      this.handleResponse(response)
    },
    handleResponse (response) {
      const data = API.responseData(response)?.bankFailuresGraphData || null

      const errors = API.responseErrors(response) || []

      if (API.isResponseSuccess(response) && data) {
        this.UsBankFailures.Data = clone(data)
      } else {
        this.$bus.$emit('app:error', errors)
      }
    }
  }
}
    </script>

    <style scoped>

  .chart-title {
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
  }

  .chart-subtitle {
    margin-left: 8px;
    color: var(--v-synthesis-ui-grey-01-base);
    font-size: 10px;
    position: relative;
    top: 1px;
  }

  .metric-label {
    font-size: 10px;
    color: var(--v-synthesis-brand-dark-blue-base);
    margin-bottom: 2px;
  }

  .chart-header {
    display: flex;
    justify-content: space-between;
    padding: 0 8px;
  }

  .chart-metric {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  .metric-value {
    font-size: 12px;
    font-weight: 500;
  }

  .metric-value.positive {
    color: #4CAF50;
    font-size: 28px;
  }

  .metric-value.negative {
    color: #FF5722;
    font-size: 28px;
  }

  .chart-legend {
    display: flex;
    gap: 12px;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    color: #666;
  }

  .legend-color {
    width: 8px;
    height: 8px;
    border-radius: 2px;
  }
  .chart-fixed-wrap {
    height: 350px;
    position: relative;
    overflow: hidden;
  }

  .chart {
    height: 100%;
    width: 100%;
    display: block;
  }
    </style>
